import { DailyWork } from './DailyWork';
import { inventoryQuery, useTodayScope } from './today-scope';
import type { SearchHit } from '@contracts';
import { ArrowRight, ArrowUpCircle, Building2, CalendarClock, CircleSlash, HardDrive, Layers, PartyPopper, Radio, ShieldAlert, Sparkles, Wrench } from 'lucide-react';
import { type JobBatchRequest, MAX_BATCH, type RunbookSummary, type SignalKey } from '@api/devices/contracts';
import { type FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { recordPath } from '../records/paths';
import { useScope } from '../scope/scope';
import { useSession } from '../session/session';
import { needsLookQuery, useAgentReleases, useCatalogRunbooks, useFacets, useSearch } from '../search/hooks';
import { BatchApprovalDialog, BatchRequestDialog } from '../operations/batch';
import { useCapabilities } from '../operations/jobs';
import { type FilterState, writeFilters } from '../search/state';
import { agentSource, useMissingSources } from '../lib/sources';
import { DAY, duration, HOUR, longDay, MINUTE } from '../lib/time';
import { cn } from '../ui/cn';
import { ErrorPanel } from '../ui/errors';
import { Card, SectionTitle, Skeleton, Spark, TimeAgo, useNow } from '../ui/misc';
import { STATUS_ORDER, StatusBadge, StatusBar, statusMeta } from '../ui/status';
import { PageHeader } from './PageHeader';

type Reason = 'attention' | 'offline' | 'stale' | 'unknown' | 'never';
interface Item { hit: SearchHit; reason: Reason; text: string }
interface Cluster { key: string; hits: SearchHit[]; place: string; spread: number; since: number }

function classify(hit: SearchHit, now: number): Item {
  const seen = hit.lastSeenAt ? now - Date.parse(hit.lastSeenAt) : null;
  // The section and badge already say what kind of problem; the note only adds what they can't.
  const top = hit.signals.find(x => x.severity !== 'info');
  if (hit.managed && hit.status === 'attention' && top) {
    return { hit, reason: 'attention', text: `${top.title}${hit.signals.length > 1 ? ` · and ${hit.signals.length - 1} more` : ''}` };
  }
  if (hit.status === 'attention') return { hit, reason: 'attention', text: seen !== null && seen > 7 * DAY ? 'Reported attention, but hasn’t checked in since — the report may be out of date' : '' };
  if (hit.status === 'offline') return { hit, reason: 'offline', text: seen === null ? 'Never checked in' : '' };
  if (seen === null) return { hit, reason: 'never', text: hit.status === 'healthy' ? 'Reports Healthy but has never checked in' : 'Never checked in · may be newly enrolled' };
  if (hit.status === 'healthy') return { hit, reason: 'stale', text: `Reports Healthy, but hasn’t checked in for ${duration(seen)}` };
  return { hit, reason: 'unknown', text: 'No status reported' };
}

/** Three or more devices at one site that stopped reporting within half an hour of each other. */
function findClusters(items: Item[], now: number): Cluster[] {
  const bySite = new Map<string, SearchHit[]>();
  for (const { hit, reason } of items) {
    if ((reason === 'offline' || reason === 'stale' || reason === 'attention') && hit.siteId && hit.lastSeenAt && now - Date.parse(hit.lastSeenAt) > HOUR) {
      bySite.set(hit.siteId, [...(bySite.get(hit.siteId) ?? []), hit]);
    }
  }
  const clusters: Cluster[] = [];
  for (const [key, hits] of bySite) {
    if (hits.length < 3) continue;
    const times = hits.map(h => Date.parse(h.lastSeenAt!)).sort((a, b) => a - b);
    if (times.at(-1)! - times[0]! > 30 * MINUTE) continue;
    clusters.push({ key, hits, place: [hits[0]!.organizationName, hits[0]!.siteName].filter(Boolean).join(' · '), spread: times.at(-1)! - times[0]!, since: times.at(-1)! });
  }
  return clusters;
}

interface BatchAsk { title: string; summary: string; action: Omit<JobBatchRequest, 'deviceIds' | 'reason'>; deviceIds: string[] }

export function TodayPage() {
  const { mspId, mspName, coverage } = useScope();
  const now = useNow(60_000);
  const scope = useTodayScope();
  const caps = useCapabilities();
  const catalog = useCatalogRunbooks();
  const releases = useAgentReleases(caps.enroll);
  const [asking, setAsking] = useState<BatchAsk | null>(null);
  const [approvingBatch, setApprovingBatch] = useState<string | null>(null);
  // The newest release, offered for every device this person sees that runs an older agent.
  const release = (scope.invalid.length || scope.query.q || scope.query.filters?.length) ? undefined : releases.data?.find(r => r.outdatedDeviceIds.length);
  const fixAll = (runbook: RunbookSummary, items: Item[]) => setAsking({ title: runbook.title, summary: runbook.summary,
    action: { runbook: { id: runbook.id, parameters: Object.fromEntries(runbook.parameters.filter(p => p.default !== undefined).map(p => [p.name, p.default!])) } }, deviceIds: items.map(i => i.hit.id) });
  const fleet = useFacets(scope.invalid.length ? null : scope.query);
  const needs = useSearch(scope.invalid.length ? null : inventoryQuery(scope.query, needsLookQuery()));
  const items = useMemo(() => (scope.invalid.length ? [] : needs.data?.hits ?? []).map(h => classify(h, now)), [needs.data, now, scope.invalid.length]);
  const clusters = useMemo(() => findClusters(items, now), [items, now]);
  const clustered = new Set(clusters.flatMap(c => c.hits.map(h => h.id)));
  const loose = items.filter(i => !clustered.has(i.hit.id));
  // The same problem on three or more devices is one item to act on, not a list to scroll.
  const repeated = useMemo(() => {
    const byTitle = new Map<string, Item[]>();
    for (const i of loose) {
      const top = i.reason === 'attention' && i.hit.managed ? i.hit.signals.find(x => x.severity !== 'info') : undefined;
      if (top) byTitle.set(`${top.key}\u0000${top.title}`, [...(byTitle.get(`${top.key}\u0000${top.title}`) ?? []), i]);
    }
    return [...byTitle.entries()].filter(([, list]) => list.length >= 3)
      .map(([key, list]) => ({ key, signal: key.split('\u0000')[0] as SignalKey, title: key.split('\u0000')[1]!, items: list }));
  }, [loose]);
  const grouped = new Set(repeated.flatMap(g => g.items.map(i => i.hit.id)));
  const sections: Array<{ key: string; title: string; note: string; items: Item[]; showClusters?: boolean; groups?: typeof repeated }> = [
    { key: 'problem', title: 'Health issues', note: 'From the agent’s health signals, or a status a device reported.',
      items: loose.filter(i => i.reason === 'attention' && !grouped.has(i.hit.id)), groups: repeated },
    // Only servers: an offline workstation or laptop is a status, shown in the device list, not a problem (WRMM-255).
    { key: 'offline', title: 'Offline servers', note: 'Grouped when several at one site stopped together.', items: loose.filter(i => i.reason === 'offline'), showClusters: true },
    { key: 'unverified', title: 'Can’t vouch for', note: 'May be fine — there just isn’t recent evidence either way.', items: loose.filter(i => ['stale', 'unknown', 'never'].includes(i.reason)) },
  ];
  const total = fleet.data?.total ?? 0;
  const needCount = needs.data?.total ?? 0;
  const byCustomer = useMemo(() => {
    const counts = new Map<string, { id: string; name: string; count: number }>();
    for (const { hit } of items) {
      const c = counts.get(hit.organizationId) ?? { id: hit.organizationId, name: hit.organizationName ?? 'Unnamed customer', count: 0 };
      c.count += 1; counts.set(hit.organizationId, c);
    }
    return [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [items]);
  const deviceTotals = new Map(fleet.data?.organizations.map(o => [o.id, o.count]));
  const filtered = (patch: Partial<FilterState>) => `/w/${mspId}/devices?${writeFilters({ ...scope.state, kind: 'device', ...patch }, { kind: 'device' })}`;
  const loading = fleet.isPending || needs.isPending;

  return (
    <div className="space-y-6">
      <PageHeader title="Today" description={`${longDay(new Date(now))} · ${mspName}${coverage === 'All customers' ? '' : ` · ${coverage}`}`} />
      <AskBox topCustomer={byCustomer[0]?.name} offline={fleet.data?.status.offline ?? 0} />
      <DailyWork />
      {release && release.outdatedDeviceIds.length > 0 && (
        <Card className="flex flex-wrap items-center gap-3 p-4">
          <ArrowUpCircle className="size-5 text-brand" />
          <p className="min-w-0 flex-1 text-[13.5px]"><span className="font-semibold">WITTCO agent {release.version} is available.</span>{' '}
            <span className="text-ink-2">{release.outdatedDeviceIds.length} Windows device{release.outdatedDeviceIds.length === 1 ? ' runs' : 's run'} an older version.</span></p>
          <button type="button" className="text-[13.5px] font-medium text-brand hover:underline" onClick={() => setAsking({ title: `Update the agent to ${release.version}`,
            summary: `Each device checks the release is signed by WITTCO, installs it, and goes back to its current version by itself if the new one doesn’t stay up.${
              release.outdatedDeviceIds.length > MAX_BATCH ? ` One approval covers up to ${MAX_BATCH} devices; the rest are offered here again afterwards.` : ''}`,
            action: { update: { version: release.version } }, deviceIds: release.outdatedDeviceIds.slice(0, MAX_BATCH) })}>Update all</button>
        </Card>
      )}

      {scope.invalid.length ? null : (fleet.isError || needs.isError) ? <Card><ErrorPanel error={fleet.error ?? needs.error} onRetry={() => { void fleet.refetch(); void needs.refetch(); }} /></Card> : (
        <details className="space-y-4">
          <summary className="cursor-pointer font-semibold">Inventory overview and grouped actions</summary>
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0 space-y-6">
            <Card className="p-5">
              {loading ? <div className="space-y-3"><Skeleton className="h-5 w-2/3" /><Skeleton className="h-2 w-full" /><Skeleton className="w-1/2" /></div> : (
                <>
                  <p className="text-[17px] leading-snug text-ink">
                    {needCount === 0 ? (fleet.data?.status.offline ? <>
                      <span className="font-semibold">Nothing needs a look</span> across {total} devices. {fleet.data.status.offline} {fleet.data.status.offline === 1 ? 'is' : 'are'} offline. Evidence coverage is above.
                    </> : <><span className="font-semibold">{total} devices</span> in this inventory scope; no status or check-in exceptions returned. See evidence coverage above.</>) : <>
                      <span className="font-semibold">{needCount} of {total} devices</span> need a look{byCustomer.length > 1 ? ` across ${byCustomer.length} customers` : ''}.
                      {clusters[0] && <> {clusters[0].hits.length} at {clusters[0].place} went quiet together about {duration(now - clusters[0].since)} ago.</>}
                    </>}
                  </p>
                  {fleet.data && <StatusBar className="mt-4 h-2.5" counts={fleet.data.status} quietOffline />}
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                    {fleet.data && STATUS_ORDER.map(s => {
                      const m = statusMeta[s], Icon = m.icon;
                      return (
                        <Link key={s} to={filtered({ status: [s] })} className="group inline-flex items-center gap-1.5 text-[13px] text-ink-2 hover:text-ink">
                          <Icon className={cn('size-3.5', s === 'offline' ? 'text-ink-3' : m.text)} /><span className="tabular font-semibold text-ink">{fleet.data!.status[s]}</span>
                          {s === 'unknown' ? 'no status' : m.label.toLowerCase()}
                        </Link>
                      );
                    })}
                  </div>
                  {fleet.data && (
                    <div className="mt-4 grid grid-cols-2 gap-2 border-t border-line pt-4 sm:grid-cols-4">
                      {([['Checked in today', fleet.data.lastSeen.day, { type: 'within', amount: 1, unit: 'd' }],
                        ['1–7 days ago', fleet.data.lastSeen.week, null],
                        ['Over a week ago', fleet.data.lastSeen.older, { type: 'older', amount: 7, unit: 'd', includeNever: false }],
                        ['Never', fleet.data.lastSeen.unknown, { type: 'never' }]] as const).map(([label, n, seen]) => {
                        const body = (<><span className="block text-[12px] text-ink-3">{label}</span><span className={cn('tabular text-lg font-semibold', label !== 'Checked in today' && n > 0 ? 'text-ink' : 'text-ink-2')}>{n}</span></>);
                        return seen ? <Link key={label} to={filtered({ seen })} className="rounded-lg px-2 py-1 hover:bg-hover">{body}</Link> : <div key={label} className="px-2 py-1">{body}</div>;
                      })}
                    </div>
                  )}
                </>
              )}
            </Card>

            {loading ? <Card className="space-y-4 p-5">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</Card>
              : needCount === 0 ? (
                <Card className="flex items-start gap-4 p-5">
                  <PartyPopper className="mt-0.5 size-5 text-healthy" />
                  <div><p className="font-semibold">Nothing in inventory needs a look.</p><p className="mt-1 text-ink-2">That covers device status and check-ins only — see what isn’t connected yet below.</p></div>
                </Card>
              ) : sections.filter(s => s.items.length || (s.showClusters && clusters.length) || s.groups?.length).map(s => (
                <section key={s.key} aria-labelledby={`today-${s.key}`} className="space-y-2">
                  <div className="flex items-baseline gap-2 px-1">
                    <h2 id={`today-${s.key}`} className="text-[15px] font-semibold">{s.title}</h2>
                    <span className="tabular text-[13px] text-ink-3">{s.items.length + (s.showClusters ? clusters.reduce((n, c) => n + c.hits.length, 0) : 0) + (s.groups ?? []).reduce((n, g) => n + g.items.length, 0)}</span>
                    <span className="hidden text-[12.5px] text-ink-3 sm:inline">· {s.note}</span>
                  </div>
                  <Card className="divide-y divide-line overflow-hidden">
                    {s.showClusters && clusters.map(c => <ClusterRow key={c.key} cluster={c} now={now} />)}
                    {s.groups?.map(g => <GroupRow key={g.key} group={g} onFixAll={caps.diagnostics ? fixAll : undefined}
                      fixes={(catalog.data ?? []).filter(r => r.effect === 'change' && r.addresses.includes(g.signal))} />)}
                    {s.items.map(item => <ItemRow key={item.hit.id} item={item} />)}
                  </Card>
                </section>
              ))}
            {needCount > (needs.data?.hits.length ?? 0) && (
              <Link to={`/w/${mspId}/devices`} className="block text-center text-[13px] font-medium text-ink-2 hover:text-ink">
                Showing the first {needs.data?.hits.length} of {needCount}. See all in Devices →
              </Link>
            )}
          </div>

          <aside className="space-y-6">
            {byCustomer.length > 0 && (
              <Card className="p-4">
                <SectionTitle aside={<Link to={`/w/${mspId}/customers`} className="text-[12.5px] text-ink-3 hover:text-ink">All customers</Link>}>Customers to look at</SectionTitle>
                <ul className="mt-3 space-y-1">
                  {byCustomer.slice(0, 6).map(c => (
                    <li key={c.id}>
                      <Link to={recordPath(mspId, 'organization', c.id)} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-hover">
                        <Building2 className="size-4 text-ink-3" />
                        <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
                        <span className="tabular text-[12.5px] text-ink-3">{c.count} of {deviceTotals.get(c.id) ?? '?'}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
            <Protection signals={fleet.data?.signals} managed={fleet.data?.agent.managed ?? 0} />
            <ComingUp scope={scope.query} />
            <Coverage agent={fleet.data?.agent} />
          </aside>
        </div>
        </details>
      )}
      {asking && <BatchRequestDialog {...asking} open onOpenChange={o => { if (!o) setAsking(null); }} onNeedsApproval={setApprovingBatch} />}
      <BatchApprovalDialog batchId={approvingBatch} open={!!approvingBatch} onOpenChange={o => { if (!o) setApprovingBatch(null); }} />
    </div>
  );
}

function AskBox({ topCustomer, offline }: { topCustomer?: string; offline: number }) {
  const { mspId } = useScope();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const suggestions = [
    topCustomer && `What needs a look at ${topCustomer}?`,
    offline > 0 && 'Which customers have offline devices?',
    'Devices that haven’t checked in this week',
    'Never reported',
  ].filter(Boolean) as string[];
  const ask = (q: string) => navigate(`/w/${mspId}/ask?${new URLSearchParams({ q })}`);
  const submit = (e: FormEvent) => { e.preventDefault(); if (text.trim()) ask(text.trim()); };
  return (
    <div className="rounded-2xl border border-line bg-panel p-2 shadow-card">
      <form onSubmit={submit} className="flex items-center gap-3 px-2">
        <Spark className="size-5 shrink-0" />
        <input value={text} onChange={e => setText(e.target.value)} aria-label="Ask about your fleet"
          placeholder="Ask about your fleet — “which Harbor Dental devices are offline?”"
          className="h-11 min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink-3" />
        <button type="submit" disabled={!text.trim()} className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-brand px-3 text-[13px] font-medium text-white transition-opacity disabled:opacity-0">
          Ask <ArrowRight className="size-3.5" />
        </button>
      </form>
      <div className="flex gap-1.5 overflow-x-auto px-2 pb-1 pt-1 [scrollbar-width:none]">
        {suggestions.map(s => (
          <button key={s} type="button" onClick={() => ask(s)}
            className="shrink-0 rounded-full border border-line px-3 py-1 text-[12.5px] text-ink-2 transition-colors hover:border-brand/40 hover:bg-brand-soft hover:text-ink">{s}</button>
        ))}
      </div>
    </div>
  );
}

function ItemRow({ item }: { item: Item }) {
  const { mspId, remember } = useScope();
  const { hit } = item;
  return (
    <Link to={recordPath(mspId, 'device', hit.id)} onClick={() => remember(hit)} className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-hover">
      <span className="hidden w-[104px] shrink-0 sm:block"><StatusBadge status={hit.status} /></span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-mono text-[13px] font-semibold">{hit.title}</span>
          <span className="truncate text-[12.5px] text-ink-3">{[hit.organizationName, hit.siteName].filter(Boolean).join(' · ')}</span>
        </span>
        {item.text && <span className="mt-0.5 block text-[13px] text-ink-2">{item.text}</span>}
      </span>
      <span className="text-right text-[13px]"><TimeAgo value={hit.lastSeenAt} /></span>
      <ArrowRight className="size-4 shrink-0 text-ink-3 opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

function GroupRow({ group, fixes, onFixAll }: { group: { signal: SignalKey; title: string; items: Item[] }; fixes: RunbookSummary[];
  onFixAll?: (runbook: RunbookSummary, items: Item[]) => void }) {
  const { mspId } = useScope();
  const scope = useTodayScope();
  const [open, setOpen] = useState(false);
  const customers = [...new Set(group.items.map(i => i.hit.organizationName).filter(Boolean))];
  const devicesLink = `/w/${mspId}/devices?${writeFilters({ ...scope.state, kind: 'device', signal: [group.signal] }, { kind: 'device' })}`;
  return (
    <div className="bg-attention-soft/30">
      <div className="flex items-start gap-3 px-4 py-3.5">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-attention-soft text-attention"><Layers className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{group.items.length} devices: {group.title}</p>
          <p className="mt-0.5 text-[13px] text-ink-2">{customers.join(', ')} · one change can likely fix them together.</p>
          <div className="mt-2 flex flex-wrap gap-3">
            <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="text-[13px] font-medium text-ink-2 hover:text-ink">{open ? 'Hide devices' : `Show ${group.items.length} devices`}</button>
            <Link to={devicesLink} className="text-[13px] font-medium text-ink-2 hover:text-ink">Open as list →</Link>
            {onFixAll && fixes.map(r => (
              <button key={r.id} type="button" onClick={() => onFixAll(r, group.items)} className="inline-flex items-center gap-1 text-[13px] font-medium text-brand hover:underline">
                <Wrench className="size-3.5" />Fix all {group.items.length}: {r.title}</button>
            ))}
          </div>
          {onFixAll && fixes.filter(r => r.caveat).map(r => <p key={`${r.id}-caveat`} data-caveat className="mt-1.5 text-[12px] text-ink-3">{r.caveat}</p>)}
        </div>
      </div>
      {open && <div className="divide-y divide-line border-t border-line bg-panel">{group.items.map(i => <ItemRow key={i.hit.id} item={i} />)}</div>}
    </div>
  );
}

function ClusterRow({ cluster, now }: { cluster: Cluster; now: number }) {
  const { mspId } = useScope();
  const [open, setOpen] = useState(false);
  const siteLink = recordPath(mspId, 'site', cluster.key);
  return (
    <div className="bg-offline-soft/40">
      <div className="flex items-start gap-3 px-4 py-3.5">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-offline-soft text-offline"><Radio className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{cluster.hits.length} devices at <Link to={siteLink} className="hover:underline">{cluster.place}</Link> went quiet together</p>
          <p className="mt-0.5 text-[13px] text-ink-2">
            Last check-ins within {duration(Math.max(cluster.spread, MINUTE))} of each other, about {duration(now - cluster.since)} ago.
            {' '}<span className="inline-flex items-center gap-1 text-ink"><Sparkles className="size-3.5 text-brand" />Likely a shared cause at the site (network or power) — check that first.</span>
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => setOpen(o => !o)} className="text-[13px] font-medium text-ink-2 hover:text-ink" aria-expanded={open}>
              {open ? 'Hide devices' : `Show ${cluster.hits.length} devices`}
            </button>
            <Link to={siteLink} className="text-[13px] font-medium text-ink-2 hover:text-ink">Open site →</Link>
          </div>
        </div>
      </div>
      {open && <div className="divide-y divide-line border-t border-line bg-panel">{cluster.hits.map(h => <ItemRow key={h.id} item={classify(h, now)} />)}</div>}
    </div>
  );
}

const PROTECTION_ROWS: Array<[SignalKey, string]> = [
  ['security.av_off', 'Antivirus not protecting'], ['security.firewall_off', 'Firewall off'], ['security.encryption_off', 'Disk not encrypted'],
  ['security.remote_inbound', 'WITTCO Remote reachable from the network'],
  ['os.unsupported', 'No longer gets security updates'], ['security.av_stale', 'Antivirus definitions out of date'], ['patch.stale', 'Updates overdue'],
  ['patch.paused', 'Windows Update paused'], ['patch.disabled', 'Windows Update turned off'], ['reboot.pending', 'Restart pending to finish updates'],
];

/** Fleet protection at a glance; every row opens the matching devices. */
function Protection({ signals, managed }: { signals?: Partial<Record<SignalKey, number>>; managed: number }) {
  const { mspId } = useScope();
  const scope = useTodayScope();
  if (!signals || !managed) return null;
  const gaps = PROTECTION_ROWS.reduce((n, [k]) => n + (signals[k] ?? 0), 0);
  const link = (key: SignalKey) => `/w/${mspId}/devices?${writeFilters({ ...scope.state, kind: 'device', signal: [key] }, { kind: 'device' })}`;
  return (
    <Card className="p-4">
      <SectionTitle>Protection</SectionTitle>
      <p className="mt-1 text-[12.5px] text-ink-3">Across {managed} devices with the agent.</p>
      <ul className="mt-2 space-y-0.5">
        {PROTECTION_ROWS.map(([key, label]) => {
          const n = signals[key] ?? 0;
          return (
            <li key={key}>
              <Link to={link(key)} className={cn('flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] hover:bg-hover', !n && 'pointer-events-none')}>
                {n ? <ShieldAlert className={cn('size-4', key.startsWith('security.av_off') || key === 'os.unsupported' ? 'text-offline' : 'text-attention')} /> : <CircleSlash className="size-4 text-ink-3" />}
                <span className={cn('min-w-0 flex-1', !n && 'text-ink-3')}>{label}</span>
                <span className={cn('tabular text-[12.5px]', n ? 'font-semibold text-ink' : 'text-ink-3')}>{n || 'None'}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      {gaps === 0 && <p className="mt-2 text-[12.5px] text-ink-3">No gaps in the recorded signals; freshness and unassessed sources remain above.</p>}
    </Card>
  );
}

/** Problems the data says are on the way: disks about to fill, operating systems about to lose support. */
function ComingUp({ scope }: { scope: import('@contracts').SearchQueryInput }) {
  const selected = useTodayScope();
  const { mspId, remember } = useScope();
  const upcoming = useSearch(inventoryQuery(scope, { version: 1, kinds: ['device'], limit: 50, sort: { field: 'title', direction: 'asc' },
    filters: [{ mode: 'and', clauses: [{ field: 'signal', op: 'in', values: ['disk.filling', 'os.ending'] }] }] }));
  const hits = upcoming.data?.hits ?? [];
  if (!hits.length) return null;
  const filling = hits.filter(h => h.signals.some(s => s.key === 'disk.filling'));
  const ending = hits.filter(h => h.signals.some(s => s.key === 'os.ending'));
  const endingTitle = ending[0]?.signals.find(s => s.key === 'os.ending')?.title;
  return (
    <Card className="p-4">
      <SectionTitle>Coming up</SectionTitle>
      <ul className="mt-2 space-y-2.5">
        {filling.map(h => (
          <li key={h.id}>
            <Link to={`${recordPath(mspId, 'device', h.id)}?tab=storage`} onClick={() => remember(h)} className="flex items-start gap-2.5 rounded-lg px-2 py-1 hover:bg-hover">
              <HardDrive className="mt-0.5 size-4 text-attention" />
              <span className="min-w-0 text-[13px]"><span className="font-mono font-semibold">{h.title}</span>
                <span className="block text-ink-2">{h.signals.find(s => s.key === 'disk.filling')!.title}</span></span>
            </Link>
          </li>
        ))}
        {ending.length > 0 && (
          <li>
            <Link to={`/w/${mspId}/devices?${writeFilters({ ...selected.state, kind: 'device', signal: ['os.ending'] }, { kind: 'device' })}`} className="flex items-start gap-2.5 rounded-lg px-2 py-1 hover:bg-hover">
              <CalendarClock className="mt-0.5 size-4 text-attention" />
              <span className="min-w-0 text-[13px]"><span className="font-semibold">{ending.length} device{ending.length === 1 ? '' : 's'}: {endingTitle?.replace(/ support ends.*/, '')}</span>
                <span className="block text-ink-2">{endingTitle?.replace(/^.* support ends/, 'Support ends')} — plan the feature update.</span></span>
            </Link>
          </li>
        )}
      </ul>
    </Card>
  );
}

function Coverage({ agent }: { agent?: { managed: number; unmanaged: number } }) {
  const { mspId } = useScope();
  const source = agentSource(agent, useSession().session.dataset);
  const missingSources = useMissingSources();
  return (
    <Card className="p-4">
      <SectionTitle>What Today can see</SectionTitle>
      <p className="mt-2 flex items-start gap-2 text-[13px]"><span className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', source.demo ? 'bg-attention' : source.connected ? 'bg-healthy' : 'bg-ink-3')} />
        <span><span className="font-medium">{source.name}</span><span className="block text-[12.5px] text-ink-3">{source.detail}</span></span></p>
      <ul className="mt-2 space-y-1.5">
        {missingSources.map(s => (
          <li key={s.key} className="flex items-center gap-2 text-[13px] text-ink-3"><CircleSlash className="size-3.5" />{s.name}</li>
        ))}
      </ul>
      <p className="mt-3 text-[12.5px] leading-relaxed text-ink-3">A quiet Today isn’t an all-clear: EDR detections and backups aren’t connected. Native tickets appear above only when the service desk is enabled. <Link to={`/w/${mspId}/settings#sources`} className="font-medium text-ink-2 hover:text-ink">Data sources</Link></p>
    </Card>
  );
}

